import React, { useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Tooltip,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

interface StructuredLogsViewerProps {
  call: any;
}

const StructuredLogsViewer: React.FC<StructuredLogsViewerProps> = ({
  call,
}) => {
  const [copyText, setCopyText] = useState("Copy Prompt");
  const [showOriginalMessages, setShowOriginalMessages] = useState(true);

  if (!call) {
    return <Typography>No call selected.</Typography>;
  }

  const llmEvent = call.llm_inference_event;
  const modelEvent = call.model_inference_event;
  const ttsEvent = call.text_to_speech_saved_event;
  const memorysearchEvent = call.active_memory_search_event;
  const audioTranscriptionEvent = call.audio_group_transcription_saved_event;
  const templatedTaskEvent = call.templated_model_task_executed_event;
  const nerRedactionEvent = call.ner_redaction_event;

  let title = "Unknown Call";
  let detailsContent = <Typography>No details available.</Typography>;

  const handleCopyPrompt = () => {
    // Always use combined messages (chat_prompt_messages) for copying
    if (llmEvent?.chat_prompt_messages) {
      const xmlPrompt = llmEvent.chat_prompt_messages
        .map(
          (message: any) =>
            `<${message.role}>\n${message.content}\n</${message.role}>`
        )
        .join("\n");

      navigator.clipboard.writeText(xmlPrompt);
      setCopyText("Copied!");
      setTimeout(() => setCopyText("Copy Prompt"), 2000); // Reset tooltip text after 2 seconds
    }
  };

  if (llmEvent) {
    title = `Model: ${llmEvent.model_name || "Unknown"}`;

    detailsContent = (
      <Box>
        {/* Display messages section */}
        {llmEvent?.chat_prompt_messages && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">
              {showOriginalMessages && llmEvent?.original_chat_prompt_messages
                ? "Block Version Messages:"
                : "Prompt Messages:"}
            </Typography>
            {/* Show original messages if they exist and showOriginalMessages is true, otherwise show combined messages */}
            {(showOriginalMessages && llmEvent?.original_chat_prompt_messages
              ? llmEvent.original_chat_prompt_messages
              : llmEvent.chat_prompt_messages
            ).map((message: any, idx: number) => (
              <Box key={idx} sx={{ pl: 2, mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {message.role}
                  {message.persona_block_id
                    ? ` (ID: ${message.persona_block_id})`
                    : ""}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {message.content}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        {llmEvent?.output && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Output:</Typography>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {llmEvent.output}
            </Typography>
          </Box>
        )}
        {llmEvent?.model_key && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Model Key:</Typography>
            <Typography variant="body2">{llmEvent.model_key}</Typography>
          </Box>
        )}
        {llmEvent?.model_provider && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Model Provider:</Typography>
            <Typography variant="body2">{llmEvent.model_provider}</Typography>
          </Box>
        )}
        {llmEvent?.temperature !== undefined && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Temperature:</Typography>
            <Typography variant="body2">{llmEvent.temperature}</Typography>
          </Box>
        )}
        {llmEvent?.hedge_info && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Hedge Information:</Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2">
                <strong>Request Type:</strong>{" "}
                {llmEvent.hedge_info.request_type}
              </Typography>
              {llmEvent.hedge_info.main_request_model_key && (
                <Typography variant="body2">
                  <strong>Main Model:</strong>{" "}
                  {llmEvent.hedge_info.main_request_model_key}
                </Typography>
              )}
              {llmEvent.hedge_info.hedging_policy?.hedged_model_name && (
                <Typography variant="body2">
                  <strong>Hedged Model:</strong>{" "}
                  {llmEvent.hedge_info.hedging_policy.hedged_model_name}
                </Typography>
              )}
              {llmEvent.hedge_info.hedging_policy?.delay_s !== undefined && (
                <Typography variant="body2">
                  <strong>Delay:</strong>{" "}
                  {llmEvent.hedge_info.hedging_policy.delay_s}s
                </Typography>
              )}
              {llmEvent.hedge_info.hedging_policy?.start_early_on_error !==
                undefined && (
                <Typography variant="body2">
                  <strong>Start Early on Error:</strong>{" "}
                  {llmEvent.hedge_info.hedging_policy.start_early_on_error
                    ? "Yes"
                    : "No"}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        {llmEvent?.tool_call_input_json && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Tool Use Input:</Typography>
            {(() => {
              try {
                const inputParsed = JSON.parse(llmEvent.tool_call_input_json);

                return Array.isArray(inputParsed) && inputParsed.length ? (
                  inputParsed.map((entry: any, idx: number) => {
                    // best label we can find for the accordion header
                    const label =
                      (entry.function && entry.function.name) ||
                      entry.type ||
                      `Entry ${idx + 1}`;

                    return (
                      <Accordion key={idx} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold" }}
                          >
                            {label}
                          </Typography>
                        </AccordionSummary>

                        <AccordionDetails>
                          <Box
                            component="pre"
                            sx={{
                              m: 0,
                              p: 2,
                              fontFamily: "monospace",
                              fontSize: "0.9rem",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              backgroundColor: "#f9f9f9",
                              borderRadius: 1,
                              border: "1px solid #ddd",
                            }}
                          >
                            {JSON.stringify(entry, null, 2)}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No structured input.
                  </Typography>
                );
              } catch {
                return (
                  <Typography variant="body2" color="error">
                    Error parsing tool_call_input_json
                  </Typography>
                );
              }
            })()}
          </Box>
        )}
        {llmEvent?.tool_call_json && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Tool Use Output:</Typography>
            {(() => {
              try {
                const outputParsed = JSON.parse(llmEvent.tool_call_json);

                /** util: if an object is { "0": "{", "1": "\n", ... } ➜ rebuild real JSON */
                const rebuildCharMap = (obj: any): any => {
                  if (obj && typeof obj === "object" && !Array.isArray(obj)) {
                    const keys = Object.keys(obj);
                    if (
                      keys.length &&
                      keys.every((k) => /^\d+$/.test(k)) // all‑numeric keys
                    ) {
                      const jsonString = keys
                        .sort((a, b) => Number(a) - Number(b))
                        .map((k) => obj[k])
                        .join("");
                      try {
                        return JSON.parse(jsonString); // return parsed JSON
                      } catch {
                        return jsonString; // fall back to raw string
                      }
                    }
                  }
                  return obj;
                };

                return Array.isArray(outputParsed) && outputParsed.length ? (
                  outputParsed.map((entry: any, idx: number) => {
                    let parsedEntry: any = { ...entry };

                    // parse function.arguments if needed
                    if (
                      parsedEntry.function &&
                      typeof parsedEntry.function === "object" &&
                      typeof parsedEntry.function.arguments === "string"
                    ) {
                      try {
                        parsedEntry.function.arguments = JSON.parse(
                          parsedEntry.function.arguments
                        );
                      } catch {
                        /* leave as string */
                      }
                    }

                    // rebuild any char‑map objects (top level or nested)
                    parsedEntry = rebuildCharMap(parsedEntry);

                    return (
                      <Box
                        key={idx}
                        sx={{
                          mb: 2,
                          p: 2,
                          borderRadius: 2,
                          backgroundColor: "#f9f9f9",
                          fontFamily: "monospace",
                          fontSize: "0.9rem",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          border: "1px solid #ddd",
                        }}
                      >
                        {JSON.stringify(parsedEntry, null, 2)}
                      </Box>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No structured output.
                  </Typography>
                );
              } catch {
                return (
                  <Typography variant="body2" color="error">
                    Error parsing tool_call_json
                  </Typography>
                );
              }
            })()}
          </Box>
        )}
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">More Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {JSON.stringify(call, null, 2)}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  } else if (modelEvent) {
    title = `Model Key: ${modelEvent.model_key || "Unknown"}`;

    detailsContent = (
      <Box>
        {modelEvent?.model_id && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Model ID:</Typography>
            <Typography variant="body2">{modelEvent.model_id}</Typography>
          </Box>
        )}

        {modelEvent?.inputs && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Inputs:</Typography>
            {modelEvent.inputs.repeated_query?.queries?.map(
              (query: string, idx: number) => (
                <Box key={idx} sx={{ pl: 2, mb: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {query}
                  </Typography>
                </Box>
              )
            )}
          </Box>
        )}
        {modelEvent?.outputs && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Outputs:</Typography>
            {modelEvent.outputs.outputs?.map((output: any, idx: number) => {
              let outputJson = {};
              if (output.json?.json) {
                try {
                  outputJson = JSON.parse(output.json.json);
                } catch (e) {
                  console.error("Error parsing output JSON:", e);
                }
              }
              return (
                <Box key={idx} sx={{ pl: 2, mb: 1 }}>
                  {Object.entries(outputJson).map(([key, value]) => (
                    <Typography
                      key={key}
                      variant="body2"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {`${key}: ${value}`}
                    </Typography>
                  ))}
                </Box>
              );
            })}
          </Box>
        )}

        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">More Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {JSON.stringify(call, null, 2)}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  } else if (ttsEvent) {
    const diagnostics = ttsEvent?.diagnostics;

    title = `TTS Saved Event - Blob: ${ttsEvent.blob_path || "Unknown"}`;
    detailsContent = (
      <Box>
        <Typography variant="subtitle1">Blob Information:</Typography>
        <Typography variant="body2">
          Container: {ttsEvent?.blob_container || "N/A"}
        </Typography>
        <Typography variant="body2">
          Path: {ttsEvent?.blob_path || "N/A"}
        </Typography>

        {diagnostics && (
          <>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Diagnostics:
            </Typography>
            <Typography variant="body2">
              Provider: {diagnostics?.provider || "N/A"}
            </Typography>
            <Typography variant="body2">
              Transcript: {diagnostics?.transcript || "N/A"}
            </Typography>
            <Typography variant="body2">
              Spoken Transcript: {diagnostics?.spoken_transcript || "N/A"}
            </Typography>
            <Typography variant="body2">
              Emotion: {diagnostics?.emotion || "N/A"}
            </Typography>
            <Typography variant="body2">
              Model ID: {diagnostics?.model_id || "N/A"}
            </Typography>
            <Typography variant="body2">
              Volume: {diagnostics?.volume || "N/A"}
            </Typography>
            <Typography variant="body2">
              Speed: {diagnostics?.speed || "N/A"}
            </Typography>
            <Typography variant="body2">
              Stability: {diagnostics?.stability || "N/A"}
            </Typography>
            <Typography variant="body2">
              Similarity Boost: {diagnostics?.similarity_boost || "N/A"}
            </Typography>
            <Typography variant="body2">
              Style Boost: {diagnostics?.style_boost || "N/A"}
            </Typography>
          </>
        )}

        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">More Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {JSON.stringify(call, null, 2)}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  } else if (memorysearchEvent) {
    title = `Memory Search Event - Policy: ${
      memorysearchEvent.policy || "N/A"
    }`;

    detailsContent = (
      <Box>
        {/* Show whichever fields matter to you: */}
        <Typography variant="subtitle1">Memory Search Details:</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Search Type: {memorysearchEvent.search_type || "Unknown"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Policy: {memorysearchEvent.policy || "N/A"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Top K: {memorysearchEvent.top_k || "N/A"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, whiteSpace: "pre-wrap" }}>
          Query: {memorysearchEvent.query || "N/A"}
        </Typography>

        {/* If you want to list found_ids: */}
        <Typography variant="subtitle2">Found IDs</Typography>
        {memorysearchEvent.found_ids?.length ? (
          memorysearchEvent.found_ids.map((id: string, i: number) => (
            <Typography key={i} variant="body2" sx={{ ml: 2 }}>
              {id}
            </Typography>
          ))
        ) : (
          <Typography variant="body2" sx={{ ml: 2 }}>
            No found IDs
          </Typography>
        )}

        {/* Expandable raw JSON block */}
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">More Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {JSON.stringify(call, null, 2)}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  } else if (audioTranscriptionEvent) {
    title = `Audio Group Transcription - Blob: ${
      audioTranscriptionEvent.blob_path || "N/A"
    }`;

    detailsContent = (
      <Box>
        <Typography variant="subtitle1">Blob Container:</Typography>
        <Typography variant="body2">
          {audioTranscriptionEvent.blob_container || "N/A"}
        </Typography>

        {audioTranscriptionEvent.diagnostics && (
          <>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Diagnostics:
            </Typography>
            <Typography variant="body2">
              Transcript:{" "}
              {audioTranscriptionEvent.diagnostics.transcript || "N/A"}
            </Typography>
            <Typography variant="body2">
              Word Count:{" "}
              {audioTranscriptionEvent.diagnostics.word_count ?? "N/A"}
            </Typography>
            <Typography variant="body2">
              Provider:{" "}
              {audioTranscriptionEvent.diagnostics.transcription_provider ||
                "N/A"}
            </Typography>
            {/* Add any other relevant fields here */}
          </>
        )}

        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">More Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >
              {JSON.stringify(call, null, 2)}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  } else if (templatedTaskEvent) {
    title = `Templated Task: ${templatedTaskEvent.template_id || "Unknown"}`;

    // Utility for pretty‑printing JSON fields if they parse
    const tryParse = (str?: string) => {
      if (!str) return null;
      try {
        return JSON.stringify(JSON.parse(str), null, 2);
      } catch {
        return str;
      }
    };

    detailsContent = (
      <Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1">Execution ID:</Typography>
          <Typography variant="body2">
            {templatedTaskEvent.execution_id}
          </Typography>
        </Box>
        {templatedTaskEvent.template_id && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Template ID:</Typography>
            <Typography variant="body2">
              {templatedTaskEvent.template_id}
            </Typography>
          </Box>
        )}
        {templatedTaskEvent.template_descriptor && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Template Descriptor:</Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {templatedTaskEvent.template_descriptor}
            </Typography>
          </Box>
        )}
        {templatedTaskEvent.model_task_type && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Model Task Type:</Typography>
            <Typography variant="body2">
              {templatedTaskEvent.model_task_type}
            </Typography>
          </Box>
        )}
        {templatedTaskEvent.dependencies &&
          templatedTaskEvent.dependencies.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">Dependencies:</Typography>
              {templatedTaskEvent.dependencies.map((dep: any, idx: number) => (
                <Typography key={idx} variant="body2" sx={{ ml: 2 }}>
                  {JSON.stringify(dep)}
                </Typography>
              ))}
            </Box>
          )}
        {templatedTaskEvent.template_args_json && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Template Args:</Typography>
            <Typography
              component="pre"
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                backgroundColor: "#f9f9f9",
                p: 1,
                borderRadius: 1,
              }}
            >
              {tryParse(templatedTaskEvent.template_args_json)}
            </Typography>
          </Box>
        )}
        {templatedTaskEvent.task_response_json && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Task Response:</Typography>
            <Typography
              component="pre"
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                backgroundColor: "#f9f9f9",
                p: 1,
                borderRadius: 1,
              }}
            >
              {tryParse(templatedTaskEvent.task_response_json)}
            </Typography>
          </Box>
        )}
        {templatedTaskEvent.parse_args_json && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Parsed Args:</Typography>
            <Typography
              component="pre"
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                backgroundColor: "#f9f9f9",
                p: 1,
                borderRadius: 1,
              }}
            >
              {tryParse(templatedTaskEvent.parse_args_json)}
            </Typography>
          </Box>
        )}
        {/* Raw JSON accordion */}
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">More Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >
              {JSON.stringify(call, null, 2)}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  } else if (nerRedactionEvent) {
    title = `NER Redaction: ${nerRedactionEvent.language || "Unknown Language"}`;

    detailsContent = (
      <Box>
        {/* Request Info */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1">Request ID:</Typography>
          <Typography variant="body2">
            {nerRedactionEvent.request_id || "N/A"}
          </Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1">Language:</Typography>
          <Typography variant="body2">
            {nerRedactionEvent.language || "N/A"}
          </Typography>
        </Box>
        {nerRedactionEvent.processing_time_ms !== undefined && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Processing Time:</Typography>
            <Typography variant="body2">
              {nerRedactionEvent.processing_time_ms} ms
            </Typography>
          </Box>
        )}

        {/* Input Texts */}
        {nerRedactionEvent.input_texts &&
          nerRedactionEvent.input_texts.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">Input Texts:</Typography>
              {nerRedactionEvent.input_texts.map((text: string, idx: number) => (
                <Box
                  key={idx}
                  sx={{
                    ml: 2,
                    mb: 1,
                    p: 1,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    {text}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

        {/* Results with Entities */}
        {nerRedactionEvent.results && nerRedactionEvent.results.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Extraction Results:</Typography>
            {nerRedactionEvent.results.map((result: any, idx: number) => (
              <Accordion key={idx} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    Text {idx + 1}:{" "}
                    {result.entities?.length || 0} entities found
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {result.text && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="textSecondary">
                        Original Text:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          backgroundColor: "#fafafa",
                          p: 1,
                          borderRadius: 1,
                        }}
                      >
                        {result.text}
                      </Typography>
                    </Box>
                  )}
                  {result.entities && result.entities.length > 0 ? (
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Entities:
                      </Typography>
                      {result.entities.map((entity: any, entityIdx: number) => (
                        <Box
                          key={entityIdx}
                          sx={{
                            ml: 2,
                            mb: 1,
                            p: 1,
                            backgroundColor: "#e3f2fd",
                            borderRadius: 1,
                            borderLeft: "3px solid #1976d2",
                          }}
                        >
                          <Typography variant="body2">
                            <strong>Text:</strong> "{entity.text}"
                          </Typography>
                          <Typography variant="body2">
                            <strong>Label:</strong>{" "}
                            <Chip
                              label={entity.label}
                              size="small"
                              color="primary"
                              sx={{ ml: 0.5 }}
                            />
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Position: {entity.start_char} - {entity.end_char}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No entities detected in this text.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        {/* Raw JSON accordion */}
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">More Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >
              {JSON.stringify(call, null, 2)}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  } else {
    // Unknown event type
    detailsContent = (
      <Box>
        <Typography variant="body2">
          No specific display logic for this event type.
        </Typography>
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">More Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {JSON.stringify(call, null, 2)}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  }

  return (
    <Box>
      {/* Title Section with Copy Prompt Button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h5">{title}</Typography>
          {llmEvent?.hedge_info?.request_type === "REQUEST_TYPE_HEDGED" && (
            <Chip
              label="HEDGED"
              color="error"
              size="small"
              sx={{ fontWeight: "bold" }}
            />
          )}
        </Box>
        {llmEvent?.chat_prompt_messages && (
          <Box sx={{ display: "flex", gap: 2 }}>
            {llmEvent?.original_chat_prompt_messages && (
              <Tooltip title="Toggle between original and combined messages">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setShowOriginalMessages(!showOriginalMessages)}
                  startIcon={<SwapHorizIcon />}
                >
                  {showOriginalMessages
                    ? "Show LLM Prompt"
                    : "Show Block Version"}
                </Button>
              </Tooltip>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleCopyPrompt}
              disabled={copyText === "Copied!"}
            >
              {copyText}
            </Button>
          </Box>
        )}
      </Box>

      {detailsContent}
    </Box>
  );
};

export default StructuredLogsViewer;
